import React, { Fragment, useEffect, useState } from 'react';
import { Row, Col, Calendar, Card, Typography, Badge, Flex, Select, List, Tag, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import { getCalendarEvents } from './redux/slice';
import { primary, secondary, warning, muted } from '../../utils/theme/config';

dayjs.extend(isSameOrBefore);

const { Title, Text } = Typography;

const EVENT_TYPES = [
    { value: '', label: 'Alle Typen' },
    { value: 'contractEnd', label: 'Vertragsende' },
    { value: 'noticePeriod', label: 'Kündigungsfrist' },
    { value: 'renewal', label: 'Verlängerung' },
    { value: 'payment', label: 'Zahlung' },
];

const EVENT_COLORS = {
    contractEnd: secondary,
    noticePeriod: '#fa8c16',
    renewal: '#1677ff',
    payment: primary,
};

const EVENT_LABELS = {
    contractEnd: 'Vertragsende',
    noticePeriod: 'Kündigungsfrist',
    renewal: 'Verlängerung',
    payment: 'Zahlung',
};

const ContractCalendar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const auth = useSelector(state => state.auth);
    const contracts = useSelector(state => state.contracts);
    const { calendarEvents, loading } = contracts;
    const organizer = auth?.decodeToken?.organizer;

    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [filterType, setFilterType] = useState('');

    useEffect(() => {
        if (organizer) {
            dispatch(getCalendarEvents(organizer));
        }
    }, [organizer]);

    const filteredEvents = (calendarEvents || []).filter((evt) => {
        if (filterType && evt.reminderType !== filterType) return false;
        return true;
    });

    const getEventsForDate = (date) => {
        const dateStr = date.format('YYYY-MM-DD');
        return filteredEvents.filter((evt) => {
            const evtDate = dayjs(evt.start).format('YYYY-MM-DD');
            return evtDate === dateStr;
        });
    };

    const getUpcoming30Days = () => {
        const today = dayjs();
        const limit = today.add(30, 'day');
        const seen = new Set();
        return filteredEvents
            .filter((evt) => {
                const d = dayjs(evt.start);
                return d.isSameOrBefore(limit) && !d.isBefore(today);
            })
            .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf())
            .filter((evt) => {
                const key = `${evt.contractId}-${evt.reminderType}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
    };

    const dateCellRender = (value) => {
        const events = getEventsForDate(value);
        if (!events.length) return null;
        return (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {events.slice(0, 3).map((evt, idx) => (
                    <li key={idx}>
                        <Badge
                            color={EVENT_COLORS[evt.reminderType] || muted}
                            text={<Text style={{ fontSize: 11 }} ellipsis>{evt.contractName || EVENT_LABELS[evt.reminderType]}</Text>}
                        />
                    </li>
                ))}
                {events.length > 3 && <Text style={{ fontSize: 11, color: muted }}>+{events.length - 3} weitere</Text>}
            </ul>
        );
    };

    const selectedDateEvents = getEventsForDate(selectedDate);
    const upcomingEvents = getUpcoming30Days();

    return (
        <Fragment>
            <Row justify='space-between' align='middle' className='bg-white padding-1 rounded-1' gutter={[16, 16]}>
                <Col>
                    <Title level={4} type='success'>Vertragskalender – Fristen</Title>
                </Col>
                <Col xs={24} sm={8} md={6}>
                    <Select
                        allowClear
                        placeholder='Ereignistyp filtern'
                        style={{ width: '100%' }}
                        options={EVENT_TYPES}
                        onChange={(val) => setFilterType(val || '')}
                    />
                </Col>
            </Row>

            <Row gutter={[16, 16]} className='padding-top-1'>
                <Col xxl={16} xl={16} lg={16} md={24} sm={24} xs={24}>
                    <Card className='border-green'>
                        {loading ? (
                            <Flex justify='center' style={{ padding: 40 }}>
                                <Spin />
                            </Flex>
                        ) : (
                            <Calendar
                                cellRender={dateCellRender}
                                onSelect={(date) => setSelectedDate(date)}
                            />
                        )}
                    </Card>
                </Col>

                <Col xxl={8} xl={8} lg={8} md={24} sm={24} xs={24}>
                    {/* Selected Date Events */}
                    <Card
                        title={
                            <Text strong>
                                Ereignisse am {selectedDate.format('DD.MM.YYYY')}
                            </Text>
                        }
                        className='border-green'
                        style={{ marginBottom: 16 }}
                    >
                        {selectedDateEvents.length === 0 ? (
                            <Text type='secondary'>Keine Ereignisse</Text>
                        ) : (
                            <List
                                dataSource={selectedDateEvents}
                                renderItem={(evt) => (
                                    <List.Item
                                        style={{ cursor: evt.contractId ? 'pointer' : 'default' }}
                                        onClick={() => evt.contractId && navigate(`/contract-manager/detail/${evt.contractId}`)}
                                    >
                                        <Flex align='center' gap={8} style={{ width: '100%' }}>
                                            <div style={{
                                                width: 10, height: 10, borderRadius: '50%',
                                                background: EVENT_COLORS[evt.reminderType] || muted,
                                                flexShrink: 0,
                                            }} />
                                            <Flex vertical style={{ flex: 1 }}>
                                                <Text strong>{evt.contractName || '—'}</Text>
                                                <Tag color={EVENT_COLORS[evt.reminderType] || muted} style={{ width: 'fit-content', marginTop: 2 }}>
                                                    {EVENT_LABELS[evt.reminderType] || evt.reminderType}
                                                </Tag>
                                            </Flex>
                                        </Flex>
                                    </List.Item>
                                )}
                            />
                        )}
                    </Card>

                    {/* Next 30 Days */}
                    <Card title={<Text strong>Nächste 30 Tage</Text>} className='border-green'>
                        {upcomingEvents.length === 0 ? (
                            <Text type='secondary'>Keine bevorstehenden Ereignisse</Text>
                        ) : (
                            <List
                                dataSource={upcomingEvents}
                                renderItem={(evt) => (
                                    <List.Item
                                        style={{ cursor: evt.contractId ? 'pointer' : 'default' }}
                                        onClick={() => evt.contractId && navigate(`/contract-manager/detail/${evt.contractId}`)}
                                    >
                                        <Flex align='center' gap={8} style={{ width: '100%' }}>
                                            <div style={{
                                                width: 10, height: 10, borderRadius: '50%',
                                                background: EVENT_COLORS[evt.reminderType] || muted,
                                                flexShrink: 0,
                                            }} />
                                            <Flex vertical style={{ flex: 1 }}>
                                                <Text strong>{evt.contractName || '—'}</Text>
                                                <Flex gap={6} align='center'>
                                                    <Text type='secondary' style={{ fontSize: 12 }}>
                                                        {dayjs(evt.start).format('DD.MM.YYYY')}
                                                    </Text>
                                                    <Tag color={EVENT_COLORS[evt.reminderType] || muted} style={{ marginLeft: 0 }}>
                                                        {EVENT_LABELS[evt.reminderType] || evt.reminderType}
                                                    </Tag>
                                                </Flex>
                                            </Flex>
                                        </Flex>
                                    </List.Item>
                                )}
                                style={{ maxHeight: 400, overflowY: 'auto' }}
                            />
                        )}
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default ContractCalendar;
